import {
	isSyncModeReason,
	SYNC_ACTIVITY_KIND,
	SYNC_MODE,
	SYNC_MODE_REASON,
	type SyncActivityRecord,
	type SyncMode,
	type SyncModeReason
} from "~/helpers/sw/messageContract";
import { latestActivityOfKind } from "~/helpers/sw/syncActivityLog";
import { stringFormatDateTime } from "~/helpers/timestampFormat";
import i18n from "~/i18n/i18n";

const REASON_KEY: Record<SyncModeReason, string> = {
	[SYNC_MODE_REASON.PERMISSION_DENIED]: "queueSync.reason.permissionDenied",
	[SYNC_MODE_REASON.REGISTRATION_FAILED]: "queueSync.reason.registrationFailed",
	[SYNC_MODE_REASON.UNSUPPORTED]: "queueSync.reason.unsupported"
};

export const formatStoragePersistedLabel = (persisted: boolean | null): string => {
	if (persisted === null) return i18n.t("storageDurability.unknown");
	return persisted ? i18n.t("storageDurability.persisted") : i18n.t("storageDurability.notPersisted");
};

const formatActivityTime = (at: number): string => stringFormatDateTime(new Date(at).toISOString());

export const formatLastBackgroundSyncLabel = (records: SyncActivityRecord[]): string => {
	const received = latestActivityOfKind(records, SYNC_ACTIVITY_KIND.SYNC_EVENT_RECEIVED);
	if (!received) return i18n.t("queueSync.activity.noSyncEvent");
	const at = formatActivityTime(received.at);
	if (received.windowCount === undefined) return at;
	const key =
		received.windowCount === 0 ? "queueSync.activity.syncEventNoWindow" : "queueSync.activity.syncEventWindow";
	return i18n.t(key, { at });
};

export const formatSyncRegistrationLabel = (records: SyncActivityRecord[]): string => {
	const failed = latestActivityOfKind(records, SYNC_ACTIVITY_KIND.REGISTRATION_FAILED);
	const succeeded = latestActivityOfKind(records, SYNC_ACTIVITY_KIND.REGISTRATION_SUCCEEDED);
	if (failed && (!succeeded || failed.at >= succeeded.at)) {
		const at = formatActivityTime(failed.at);
		if (!isSyncModeReason(failed.detail)) return i18n.t("queueSync.activity.registrationFailedUnknown", { at });
		return i18n.t("queueSync.activity.registrationFailed", { at, reason: i18n.t(REASON_KEY[failed.detail]) });
	}
	if (succeeded) return i18n.t("queueSync.activity.registrationOk", { at: formatActivityTime(succeeded.at) });
	return i18n.t("queueSync.activity.noRegistration");
};

const formatSyncModeLabel = (mode: SyncMode | null, reason: SyncModeReason | null): string => {
	if (mode === null) return i18n.t("queueSync.modeUnknown");
	if (mode === SYNC_MODE.BACKGROUND_SYNC) return i18n.t("queueSync.modeBackgroundSync");
	if (!reason) return i18n.t("queueSync.modeForegroundOnly");
	return i18n.t("queueSync.foregroundOnlyWithReason", { reason: i18n.t(REASON_KEY[reason]) });
};

export default formatSyncModeLabel;
